import { Status } from '@/Enums/Enums';
import type { LVColumn, Property } from '@/Typings';
import type { PropertyRow } from '@/Datastructures';
import type {
    ControlViewModel,
    LabelViewModel as LabelViewModelT,
    TextBoxViewModel as TextBoxViewModelT,
    CheckBoxViewModel as CheckBoxViewModelT,
    DropDownListViewModel as DropDownListViewModelT,
    NumericTextBoxViewModel as NumericTextBoxViewModelT,
    MLStringPopupBoxViewModel as MLStringPopupBoxViewModelT,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    Utilities,
    ImageHandler,
    RADApplication,
    ComponentManager,
    CheckBoxViewModelBuilder,
    LocatorHelper,
} from 'radpresentation.htmlclient.defaultviewextensions';
import MemberTabViewModel from '@/ViewModels/Controls/MemberTabViewModel';
import type EditMaskTabViewModel from '@/ViewModels/Controls/EditMaskTabViewModel';
import type ListViewTabViewModel from '@/ViewModels/Controls/ListViewTabViewModel';

class PropertyTabViewModel extends MemberTabViewModel {
    private DropDownListViewModel = ComponentManager.getViewModel<
        typeof DropDownListViewModelT
    >('DropDownListViewModel');
    private NumericTextBoxViewModel = ComponentManager.getViewModel<
        typeof NumericTextBoxViewModelT
    >('NumericTextBoxViewModel');

    private BedingungLabel: LabelViewModelT;
    private Bedingung: TextBoxViewModelT;
    private FehlerhinweisLabel: LabelViewModelT;
    private Fehlerhinweis: MLStringPopupBoxViewModelT;
    private DatatypeLabel: LabelViewModelT;
    private Datatype: DropDownListViewModelT;
    private MaxLengthLabel: LabelViewModelT;
    private MaxLength: NumericTextBoxViewModelT;
    public Required: CheckBoxViewModelT;
    private RequiredWhenLabel: LabelViewModelT;
    private RequiredWhen: TextBoxViewModelT;
    private Indexable: CheckBoxViewModelT;

    // injected
    public editMaskTabViewModel: EditMaskTabViewModel;
    public listViewTabViewModel: ListViewTabViewModel;

    public constructor(locator: KnockoutObservable<string>) {
        super(locator);

        this.createDataType();
        this.createMaxLength();
        this.createBedingung();
        this.createFehlerhinweis();
        this.createRequired();
        this.createRequiredWhen();
        this.createIndexable();

        this.setUpEnabledStatesConfiguration();
        this.syncFocusableToDisabled(this.fieldsWithCustomEnabledState);
        this.syncFocusableToDisabled(this.editableStaticPropertyFields);
        this.syncFocusableToDisabled(this.nonEditableStaticPropertyFields);

        this.currentLanguage = RADApplication.getInstance().currentLanguage();
    }

    public dispose() {
        super.dispose();
        this.disposeSubscriptions();
    }

    public onPropertyNameFinalized() {
        [
            this.Help,
            this.Fehlerhinweis,
            this.Tooltip,
            this.ErgonomicIdentifier,
        ].forEach(viewModel =>
            viewModel.multilineTextBox.value.valueHasMutated()
        );
    }

    public updateContent(relatedData?: PropertyRow) {
        this.isInUpdateMode = true;

        if (!relatedData) {
            relatedData = {} as PropertyRow;
        }
        this.relatedData = relatedData;

        super.updateContent();
        this.updateMLStringPopupBox(
            this.Fehlerhinweis,
            'ConstraintMessageByLanguage'
        );
        this.Name.value(relatedData.Name || '');
        this.Bedingung.value(relatedData.ConstraintValue || '');
        this.Datatype.value(relatedData.DotNetTypeName || '');
        this.MaxLength.value(
            relatedData.MaxLength ? relatedData.MaxLength.toString() : '0'
        );

        this.Required.isChecked(relatedData.Required == true);
        this.RequiredWhen.value(relatedData.RequiredWhen || '');
        this.Indexable.isChecked(relatedData.Indexable == true);

        this.updateEnabledStates(relatedData);

        this.isInUpdateMode = false;
    }

    private updateEnabledStates(relatedData: PropertyRow) {
        if (_.isEmpty(relatedData)) {
            return;
        }

        let isDynamic = relatedData.Dynamisch === true;

        // filter
        let hasNoCustomEnabledState = (field: ControlViewModel) =>
            !_.contains(this.fieldsWithCustomEnabledState, field);

        this.editableStaticPropertyFields
            .filter(field => hasNoCustomEnabledState(field))
            .forEach(field => field.isEnabled(true));

        this.nonEditableStaticPropertyFields
            .filter(field => hasNoCustomEnabledState(field))
            .forEach(field => field.isEnabled(isDynamic));

        this.Name.isEnabled(isDynamic && Utilities.isBlank(relatedData.Name));
        this.Datatype.isEnabled(isDynamic && relatedData.Status == Status.New);
    }

    public disableAllControls() {
        this.fieldsWithCustomEnabledState.forEach(field =>
            field.isEnabled(false)
        );
        this.editableStaticPropertyFields.forEach(field =>
            field.isEnabled(false)
        );
        this.nonEditableStaticPropertyFields.forEach(field =>
            field.isEnabled(false)
        );
    }

    private setUpEnabledStatesConfiguration() {
        this.fieldsWithCustomEnabledState = [this.Name, this.Datatype];

        this.nonEditableStaticPropertyFields = [
            this.Name,
            this.Datatype,
            this.MaxLength,
            this.Fehlerhinweis,
            this.Fehlerhinweis.multilineTextBox,
            this.Fehlerhinweis.link,
            this.Bedingung,
            this.RequiredWhen,
        ];

        this.editableStaticPropertyFields = [
            this.ErgonomicIdentifier,
            this.ErgonomicIdentifier.multilineTextBox,
            this.ErgonomicIdentifier.link,
            this.Tooltip,
            this.Tooltip.multilineTextBox,
            this.Tooltip.link,
            this.Help,
            this.Help.multilineTextBox,
            this.Help.link,
            this.Required,
            this.Indexable,
        ];
    }

    protected createName() {
        super.createName();

        this.Name.onFocusOut = () => {
            if (this.relatedData.Dynamisch == false) {
                return;
            }

            let value = this.Name.value();
            if (Utilities.isNotBlank(value) && this.Name.isEnabled()) {
                this.Name.isEnabled(false);
                this.relatedData.nameFinalizedHandler(this.Name.value());
            }
        };
    }

    private createDataType() {
        let toUrl = url =>
            ImageHandler.convertSource(
                '$$TEMPLATE_HANDLER$$?TemplateName=$$TEMPLATE_STYLE$$&ImageName=' +
                    url,
                false
            );
        let iconUrlTemplate =
            '# if (data.iconUrl) { #<img src=\"#:data.iconUrl#\"># } #<span>#:data.objectIdentValue#</span>';
        let createOption = (
            pdoId: string,
            objectIdentValue: string,
            icon: string
        ) => {
            return {
                pdoId: pdoId,
                objectIdentValue: objectIdentValue,
                iconUrl: toUrl(icon),
            } as DataTypeOption;
        };

        this.DatatypeLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.DATATYPELABEL'
        );
        this.Datatype = new this.DropDownListViewModel({
            data: [
                createOption('System.Boolean', 'boolean', 'boolean.png'),
                createOption(
                    'RAD.RADFoundation.ProblemDomain.Financial.CurrencyT',
                    'Currency',
                    'currency.png'
                ),
                createOption('System.DateTime', 'DateTime', 'datetime.png'),
                createOption(
                    'System.Nullable`1[[System.DateTime, mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089]]',
                    'Nullable<DateTime>',
                    'datetime.png'
                ),
                createOption('System.Single', 'float', 'datatype.png'),
                createOption('System.Int32', 'int', 'datatype.png'),
                createOption(
                    'RAD.RADFoundation.ProblemDomain.MLString',
                    'Multilingual String',
                    'mlstring.png'
                ),
                createOption(
                    'RAD.RADFoundation.ProblemDomain.PDEmbeddedMedium',
                    'Embedded Medium',
                    'FileTypes/page_white.png'
                ),
                createOption(
                    'RAD.RADFoundation.ProblemDomain.PDEmbeddedFileLink',
                    'Embedded File Link',
                    'FileTypes/page_white.png'
                ),
                createOption('System.String', 'String', 'string.png'),
            ],
            dataTextField: 'objectIdentValue',
            dataValueField: 'pdoId',
            isEnabled: false,
            valueTemplate: iconUrlTemplate,
            template: iconUrlTemplate,
            locator: LocatorHelper.buildLocator(this.locator, 'prop_datatype'),
        });

        let subscription = this.Datatype.selected.subscribe(
            (newValue: DataTypeOption) => {
                if (this.isInUpdateMode) {
                    return;
                }

                this.updatePropertyInJSON('DotNetTypeName', newValue.pdoId);

                this.relatedData.ChildNodes = [];
                this.relatedData.DotNetTypeName = newValue.pdoId;
                this.relatedData.Typ = newValue.objectIdentValue;
                this.relatedData.tooltip = this.context.buildCellTooltip(
                    newValue.pdoId
                );

                this.context.refreshPropertiesGrid();

                // Child-Nodes leeren, sodass im Falle eines bereits konfigurierten Multilingual-Strings keine Kindelemente zum Server gesendet werden
                this.DVGItem.ChildNodes = [];

                this.editMaskTabViewModel.updateUiGeneratorOptionsByType();
                this.editMaskTabViewModel.selectDefaultUiGenerators();

                this.updateLVColumn(newValue);
            }
        );

        this.subscriptions.push(subscription);
    }

    private updateLVColumn(dataType: DataTypeOption): void {
        let lvColumnDto = this.context.findLVColumnByName(
            this.relatedData.Name
        );
        if (!lvColumnDto) {
            return;
        }

        // update json
        lvColumnDto.UIGenerator =
            this.getLvColumnRowUiGeneratorByPropertyDataType(
                dataType.objectIdentValue
            );
        lvColumnDto.DotNetTypeName =
            this.getLvColumnRowDotNetTypeNameByPropertyDataType(
                dataType.objectIdentValue
            ) || 'System.String';
        this.updateLVColumnName(lvColumnDto, dataType);
        lvColumnDto.ListViewColumnFactory =
            this.getLvColumnFactoryByPropertyDataType(
                dataType.objectIdentValue
            );

        // update ui
        let lvColumnRow = this.context.findLVColumnRowByName(
            this.relatedData.Name
        );
        if (lvColumnRow) {
            lvColumnRow.Typ = this.context.resolveColumnType(lvColumnDto);
            this.listViewTabViewModel.refreshGrid();
        }
    }

    public updateLVColumnName(
        column: LVColumn,
        dataType: DataTypeOption = this.Datatype.selected() as DataTypeOption
    ): void {
        const suffix = this.getLvColumnRowSuffixByPropertyDataType(
            dataType.objectIdentValue
        );
        column.Name = this.setLVColumnRowSuffix(column.Name, suffix);
    }

    private setLVColumnRowSuffix(name: string, suffix: string): string {
        const kv = this.getAllLvColumnRowSuffixes();

        for (let value of _.values(kv)) {
            name.replace(`.${value}`, '');
        }

        if (Utilities.isNullOrUndefined(suffix) || Utilities.isBlank(suffix))
            return name;

        return `${name}.${suffix}`;
    }

    private getLvColumnRowUiGeneratorByPropertyDataType(
        propertyDataType: string
    ) {
        const kv = {
            boolean: 'CheckBox',
            Currency: 'CurrencyTextBox',
            CurrencyT: 'CurrencyTextBox',
            DateTime: 'DatePicker',
            'Nullable<DateTime>': 'DatePicker',
            float: 'NumericTextBox',
            int: 'NumericTextBox?FloatingPrecision=0',
            'Multilingual String': 'MLStringBox',
            'Embedded Medium': 'TextBox',
            'Embedded File Link': 'TextBox',
            String: 'TextBox',
        };

        if (!kv.hasOwnProperty(propertyDataType)) return null;

        return kv[propertyDataType];
    }

    private getLvColumnFactoryByPropertyDataType(propertyDataType: string) {
        const kv = {
            boolean: '',
            Currency: '',
            CurrencyT: '',
            DateTime: 'DateTimeColumn',
            'Nullable<DateTime>': 'DateTimeColumn',
            float: 'NumericColumn',
            int: 'NumericColumn?FloatingPrecision=0',
            'Multilingual String': '',
            'Embedded Medium': 'EmbeddedMediumLinkColumn',
            'Embedded File Link': 'EmbeddedMediumLinkColumn',
            String: '',
        };

        if (!kv.hasOwnProperty(propertyDataType)) return null;

        return kv[propertyDataType];
    }

    private getAllLvColumnRowSuffixes() {
        return {
            boolean: '',
            Currency: 'ListViewValue',
            CurrencyT: 'ListViewValue',
            DateTime: '',
            'Nullable<DateTime>': '',
            float: '',
            int: '',
            'Multilingual String': 'Value',
            'Embedded Medium': 'Filename',
            'Embedded File Link': 'Filename',
            String: '',
        };
    }

    private getLvColumnRowSuffixByPropertyDataType(propertyDataType: string) {
        const kv = this.getAllLvColumnRowSuffixes();

        if (!kv.hasOwnProperty(propertyDataType)) return null;

        return kv[propertyDataType];
    }

    private getLvColumnRowDotNetTypeNameByPropertyDataType(
        propertyDataType: string
    ) {
        const kv = {
            boolean: 'System.Boolean',
            Currency:
                'System.Nullable`1[[System.Decimal, mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089]]',
            DateTime: 'System.DateTime',
            'Nullable<DateTime>':
                'System.Nullable`1[[System.DateTime, mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089]]',
            float: 'System.Single',
            int: 'System.Int32',
            'Multilingual String': 'System.String',
            'Embedded Medium': 'System.String',
            'Embedded File Link': 'System.String',
            String: 'System.String',
        };

        if (!kv.hasOwnProperty(propertyDataType)) return null;

        return kv[propertyDataType];
    }

    private createMaxLength() {
        this.MaxLengthLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.MAXLENGTH'
        );
        this.MaxLength = new this.NumericTextBoxViewModel({
            showSpinners: true,
            min: 0,
            max: 100 * 1000,
            floatingPrecision: 0,
            value: 0,
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'maxlength'),
        });

        let subscription = this.MaxLength.value.subscribe(newValue => {
            if (this.isInUpdateMode) {
                return;
            }

            this.updatePropertyInJSON('Length', newValue);
            this.relatedData.MaxLength = parseInt(newValue);
        });
        this.subscriptions.push(subscription);
    }

    private createBedingung() {
        this.BedingungLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.CONSTRANT'
        );
        this.Bedingung = new this.TextBoxViewModel({
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'constraint'),
        });

        let subscription = this.Bedingung.value.subscribe(newValue => {
            if (this.isInUpdateMode) {
                return;
            }

            this.updatePropertyInJSON('Constraint', newValue);
            this.relatedData.ConstraintValue = newValue;
        });
        this.subscriptions.push(subscription);
    }

    private createFehlerhinweis() {
        this.FehlerhinweisLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.CONSTRAITMESSAGE'
        );
        this.Fehlerhinweis = new this.MLStringPopupBoxViewModel({
            isMultiline: false,
            isEnabled: false,
            locator: LocatorHelper.buildLocator(
                this.locator,
                'constraintMsgPopup'
            ),
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: false,
                isEnabled: false,
                locator: LocatorHelper.buildLocator(
                    this.locator,
                    'constraintMsgMLTextBox'
                ),
            }),
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });
        this.Fehlerhinweis.link.isEnabled(false);

        let resourceIdGetter = (() =>
            this.context.getPropertyMetaByPdoId(
                this.DVGItem.Member.PDO_ID.toString()
            ).ConstraintMessage).bind(this);
        this.addMultilingualFieldSync(
            this.Fehlerhinweis,
            'ConstraintMessageResourceItems',
            'ConstraintMessageByLanguage',
            resourceIdGetter
        );
    }

    private createRequired() {
        this.Required = new CheckBoxViewModelBuilder()
            .withLabel(
                this.LabelViewModel.create('RADEM.RADEXTENSIONMANAGER.REQUIRED')
            )
            .withEnabledState(false)
            .withIsCheckedSubscription(
                this.requiredCheckedSubscription.bind(this)
            )
            .withLocator(LocatorHelper.buildLocator(this.locator, 'required'))
            .create();
    }

    private createIndexable() {
        this.Indexable = new CheckBoxViewModelBuilder()
            .withLabel(
                this.LabelViewModel.create(
                    'RADEM.RADEXTENSIONMANAGER.INDEXABLE'
                )
            )
            .withEnabledState(false)
            .withIsCheckedSubscription(
                this.indexableCheckedSubscription.bind(this)
            )
            .withLocator(LocatorHelper.buildLocator(this.locator, 'indexable'))
            .create();
    }

    private createRequiredWhen() {
        this.RequiredWhenLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.REQUIREDWHEN'
        );
        this.RequiredWhen = new this.TextBoxViewModel({
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'requiredWhen'),
        });

        let subscription = this.RequiredWhen.value.subscribe(newValue => {
            if (this.isInUpdateMode) {
                return;
            }

            this.updatePropertyInJSON('RequiredWhen', newValue);
            this.relatedData.RequiredWhen = newValue;
        });
        this.subscriptions.push(subscription);
    }

    private requiredCheckedSubscription(isChecked: boolean): void {
        if (this.isInUpdateMode) {
            return;
        }

        this.updatePropertyInJSON('Required', isChecked);
        this.relatedData.Required = isChecked;

        if (isChecked) {
            this.editMaskTabViewModel.VisibleInEditMode.isChecked(true);
            this.editMaskTabViewModel.VisibleInEditMode.isEnabled(false);
        } else {
            // re-evaluate isEnabled-rule for visibleInEditMode
            this.editMaskTabViewModel.VisibleInEditMode.isEnabled(true);
        }
    }

    private indexableCheckedSubscription(isChecked: boolean): void {
        if (this.isInUpdateMode) {
            return;
        }

        this.updatePropertyInJSON('Indexable', isChecked);
        this.relatedData.Indexable = isChecked;
    }

    private updatePropertyInJSON(fieldName: keyof Property, value: any) {
        let property = this.getProperty();

        property[fieldName as any] = value;

        Status.setChanged(property);
    }

    private getProperty(): Property {
        let context = this.context;
        let memberPdoId = this.DVGItem.Member.PDO_ID;

        return context.getPropertyMetaByPdoId(memberPdoId);
    }
}

interface DataTypeOption {
    pdoId: string;
    objectIdentValue: string;
}

export default PropertyTabViewModel;
